import { z } from "zod";

/** ISO-8601 timestamp stored as text in D1. */
export const timestamp = z.string().datetime({ offset: true });

/** The four content resources that carry slugs + tags. */
export const contentType = z.enum(["vault", "prompt", "ritual", "project"]);
export type ContentType = z.infer<typeof contentType>;

/** A URL-safe human slug: lowercase words joined by single hyphens. */
export const slugSchema = z
  .string()
  .min(1)
  .max(120)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "must be a lowercase, hyphenated slug");

/** Tag names as they travel on the wire for a content entity. */
export const tagsSchema = z.array(z.string().min(1).max(60)).max(20);

/** Optional slug on inputs — the service auto-suggests one from the title. */
export const slugInput = slugSchema.optional();

/** Fields every content row shares. */
export const baseEntity = z.object({
  id: z.string().min(1),
  /** Human, URL-safe deep-link handle, unique within the resource. */
  slug: slugSchema,
  /** Shared tag vocabulary (tag names) attached to this entry. */
  tags: z.array(z.string()),
  published: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: timestamp,
  updatedAt: timestamp,
});
export type BaseEntity = z.infer<typeof baseEntity>;

/** A tag in the shared vocabulary. */
export const tag = z.object({
  id: z.string().min(1),
  name: z.string().min(1).max(60),
  slug: slugSchema,
  createdAt: timestamp,
});
export type Tag = z.infer<typeof tag>;

/** A generic success/error envelope is avoided; instead we return data directly
 *  and use HTTP status codes. Errors use this shape. */
export const apiError = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional(),
  }),
});
export type ApiError = z.infer<typeof apiError>;

export const idParam = z.object({ id: z.string().min(1) });
export type IdParam = z.infer<typeof idParam>;

export const slugParam = z.object({ slug: z.string().min(1) });
export type SlugParam = z.infer<typeof slugParam>;

/** Default and maximum page sizes for server-side pagination. */
export const DEFAULT_PAGE_SIZE = 12;
export const MAX_PAGE_SIZE = 100;

/**
 * Base list query: page (1-based) + pageSize + the admin-only unpublished flag.
 * Query params arrive as strings, so we coerce. Resource-specific queries
 * (e.g. vault's `kind`) extend this.
 */
export const listQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce
    .number()
    .int()
    .min(1)
    .max(MAX_PAGE_SIZE)
    .default(DEFAULT_PAGE_SIZE),
  includeUnpublished: z
    .enum(["true", "false"])
    .optional()
    .transform((v) => v === "true"),
});
export type ListQuery = z.infer<typeof listQuery>;

/** Vault list also filters by kind server-side so pagination stays correct. */
export const vaultListQuery = listQuery.extend({
  kind: z.enum(["note", "tool", "read", "reference"]).optional(),
});
export type VaultListQuery = z.infer<typeof vaultListQuery>;

/** Server-side pagination envelope returned by every list endpoint. */
export interface PaginatedResult<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

/** Params accepted by client list methods (all optional). */
export interface ListParams {
  page?: number;
  pageSize?: number;
  includeUnpublished?: boolean;
}
export interface VaultListParams extends ListParams {
  kind?: "note" | "tool" | "read" | "reference";
}
